import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { head, keys } from 'lodash';
import {
  addMonths,
  subMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  getDate,
  isBefore,
  format,
  getWeeksInMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { useBuilderStore } from './store';
import { formatDate } from './utils';
import Tooltip from 'components/v2/Tooltip';
import ChevronLeftIcon from './icons/ChevronLeft';
import ChevronRightIcon from './icons/ChevronRight';

const CELL_SIZE = 30;
const useStyles = makeStyles(
  theme => ({
    root: {
      width: 282,
      padding: 20,
      '& header': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
        '& svg': {
          width: 9,
          height: 'auto',
        },
      },
    },
    arrow: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        transition: theme.transitions.create(['color']),
      },
      '&:hover svg': {
        color: theme.palette.secondary.main,
      },
    },
    days: {
      display: 'grid',
      gridTemplateColumns: `repeat(7, ${CELL_SIZE}px)`,
      gridTemplateRows: 20,
      borderBottom: '1px solid #ccc',
      marginBottom: 10,
      gap: 5,
      alignItems: 'center',
      justifyItems: 'center',
    },
    body: {
      display: 'grid',
      gridTemplateColumns: `repeat(7, ${CELL_SIZE}px)`,
      gridAutoRows: CELL_SIZE,
      gap: 5,
      height: '100%',
      justifyItems: 'center',
      alignItems: 'center',
    },
    header: {
      position: 'relative',
      lineHeight: 1,
    },
    headerLoader: {
      position: 'absolute',
      right: -20,
      top: 0,
    },

    // day styles
    rootDay: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    day: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      padding: 5,
      border: '1px solid #545E69',
      cursor: 'pointer',
      transition: theme.transitions.create(['background-color', 'border']),
      '&.available': {
        border: '1px solid #545E69',
        backgroundColor: '#fff',
      },
    },
    unavailable: {
      border: `1px solid ${theme.palette.error.main}`,
      color: theme.palette.error.main,
      backgroundColor: '#F3DADA',
      cursor: 'not-allowed',
    },
    ownDate: {
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    otherDate: {
      color: 'rgba(0, 0, 0, 0.2)',
      border: '1px solid #DCDFE1',
      backgroundColor: '#DCDFE1',
    },
  }),
  {
    name: 'IB2Cal',
  },
);

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function OwnAvailabilityCalendar({ item, onClick }) {
  const classes = useStyles();
  const supplierCode = 'OWN';

  const date = useBuilderStore(state => state.data.date);
  const segmentsById = useBuilderStore(state => state.data.segmentsById);
  const segmentIdsByDate = useBuilderStore(state => state.ui.dateMap);

  const [currentDate, setCurrentDate] = useState(date || new Date());
  const handleNext = () => setCurrentDate(addMonths(currentDate, 1));
  const handlePrev = () => setCurrentDate(subMonths(currentDate, 1));

  useEffect(() => {
    if (!date) return;
    setCurrentDate(date);
  }, [date, setCurrentDate]);

  const selectedStatusByDate = keys(segmentIdsByDate).reduce((agg, date) => {
    const segmentId = segmentIdsByDate[date];
    const segment = segmentsById[segmentId];
    const selectedStatus = segment?.supplierCode === supplierCode ? 'isOwnDate' : 'isOtherDate';
    agg[date] = selectedStatus;
    return agg;
  }, {});

  const start = startOfWeek(startOfMonth(currentDate));
  const weeks = getWeeksInMonth(currentDate);
  const delta = 6 - weeks;
  const end = addWeeks(endOfWeek(endOfMonth(currentDate)), delta);

  const datesInCalendar = eachDayOfInterval({
    start,
    end,
  });

  const now = new Date();
  const daysInPast = eachDayOfInterval({
    start,
    end,
  }).reduce((agg, d) => {
    agg[formatDate(d)] = isBefore(d, now);
    return agg;
  }, {});

  const renderData = {
    isInPast: {
      class: classes.unavailable,
      tooltip: 'This date is in the past',
    },
    isSelected: {
      class: classes.ownDate,
      tooltip: 'This date is selected',
    },
    isSelectedByOtherSegment: {
      class: classes.otherDate,
      tooltip: 'This date is selected by another segment',
    },
    isAvailable: {
      class: 'available',
      tooltip: 'This date is available',
    },
  };

  const handleClickDay = day => {
    if (day.status === 'isInPast' || day.status === 'isSelectedByOtherSegment') return;
    onClick(item, day.date);
  };

  const getRenderDataForDay = date => {
    const dateKey = formatDate(date);
    const isInPast = daysInPast[formatDate(date)];
    const isSelected = selectedStatusByDate[dateKey] === 'isOwnDate';
    const isSelectedByOtherSegment = selectedStatusByDate[dateKey] === 'isOtherDate';
    const status = head(
      [
        // order of precedence
        isInPast && 'isInPast',
        isSelected && 'isSelected',
        isSelectedByOtherSegment && 'isSelectedByOtherSegment',
        'isAvailable',
      ].filter(Boolean),
    );
    const { class: uniqueClass, tooltip } = renderData[status];
    const allClasses = clsx(classes.day, uniqueClass);
    return { classes: allClasses, tooltip, status };
  };

  const days = datesInCalendar.map(d => {
    const { classes, tooltip, status } = getRenderDataForDay(d);
    return {
      date: d,
      class: classes,
      tooltip: tooltip,
      status,
    };
  });

  return (
    <div className={clsx(classes.root, 'ib2-cal')} data-test-id="OwnAvailabilityCalendar">
      <header>
        <div className={classes.arrow} onClick={handlePrev}>
          <ChevronLeftIcon />
        </div>
        <div className={classes.header}>{format(currentDate, 'MMMM Y')} </div>
        <div className={classes.arrow} onClick={handleNext}>
          <ChevronRightIcon />
        </div>
      </header>
      <div className={clsx(classes.days, 'ib-cal-days')}>
        {DAYS_OF_WEEK.map(day => (
          <div key={day}>{day}</div>
        ))}
      </div>
      <div className={clsx(classes.body, 'ib2-cal-body')}>
        {days.map(day => {
          return (
            <div className={classes.rootDay} key={day.date.toString()}>
              <Tooltip title={day.tooltip}>
                <div data-test-id={`day-${day.status}`} className={day.class} onClick={() => handleClickDay(day)}>
                  {getDate(day.date)}
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
}
